<template>
    <Teleport to="body">
        <div
            v-if="aberto"
            ref="overlayEl"
            class="modo-apresentacao-overlay"
            tabindex="-1"
            role="dialog"
            aria-modal="true"
            aria-label="Modo apresentação — Gestão de pacotes de trabalho"
        >
            <header class="apresentacao-topo">
                <div class="apresentacao-logo-wrap">
                    <img
                        src="@/assets/img/roboflex/logo-completo-preto.png"
                        alt="Roboflex"
                        class="apresentacao-logo"
                    />
                </div>
                <div class="apresentacao-topo-acoes">
                    <button type="button" class="acao-secundaria" @click="editorAberto = !editorAberto">
                        <i class="bi" :class="editorAberto ? 'bi-eye' : 'bi-pencil'" aria-hidden="true"></i>
                        {{ editorAberto ? 'Visualizar' : 'Editar' }}
                    </button>
                    <button type="button" class="acao-secundaria" :disabled="exportando" @click="exportarPdf">
                        <i class="bi bi-file-earmark-pdf" aria-hidden="true"></i>
                        {{ exportando ? 'Gerando…' : 'Exportar PDF' }}
                    </button>
                    <button type="button" class="acao-secundaria" @click="fechar">
                        <i class="bi bi-x-lg" aria-hidden="true"></i>
                        Sair
                    </button>
                </div>
            </header>

            <div class="apresentacao-corpo" :class="{ 'apresentacao-corpo--com-editor': editorAberto }">
                <main class="apresentacao-slide-area">
                    <button
                        type="button"
                        class="acao-secundaria apresentacao-nav apresentacao-nav--ant"
                        :disabled="slideAtual <= 0"
                        aria-label="Slide anterior"
                        @click="slideAnterior"
                    >
                        <i class="bi bi-chevron-left" aria-hidden="true"></i>
                    </button>

                    <div class="apresentacao-slide-container">
                        <SlideApresentacaoRenderer
                            v-if="slideResolvidoAtual"
                            :slide="slideResolvidoAtual"
                        />
                    </div>

                    <button
                        type="button"
                        class="acao-secundaria apresentacao-nav apresentacao-nav--prox"
                        :disabled="slideAtual >= slidesResolvidos.length - 1"
                        aria-label="Próximo slide"
                        @click="slideProximo"
                    >
                        <i class="bi bi-chevron-right" aria-hidden="true"></i>
                    </button>
                </main>

                <EditorApresentacaoGestao
                    v-if="editorAberto"
                    v-model:slide-ativo-idx="slideAtual"
                    :slides="config.slides"
                    :periodo-inicio="periodoInicio"
                    :periodo-fim="periodoFim"
                    :cache-projetos="cacheProjetos"
                    :cache-entregas="cacheEntregas"
                    :contagens-painel="contagensPainel"
                    @atualizar="onSlidesAtualizados"
                    @projeto-cache="onProjetoCache"
                    @entrega-cache="onEntregaCache"
                    @fechar="editorAberto = false"
                />
            </div>

            <footer class="apresentacao-rodape">
                <div class="apresentacao-indicadores">
                    <button
                        v-for="(s, idx) in config.slides"
                        :key="s.id"
                        type="button"
                        class="acao-secundaria apresentacao-bolinha"
                        :class="{ 'apresentacao-bolinha--ativa': idx === slideAtual }"
                        :aria-label="`Ir para slide ${idx + 1}`"
                        @click="slideAtual = idx"
                    />
                </div>
                <span class="apresentacao-contador">{{ slideAtual + 1 }} / {{ config.slides.length }}</span>
            </footer>
        </div>
    </Teleport>
</template>

<script>
import SlideApresentacaoRenderer from './SlideApresentacaoRenderer.vue'
import EditorApresentacaoGestao from './EditorApresentacaoGestao.vue'
import serviceProjetos from '@/services/serviceProjetos'
import {
    montarCacheEntregasDeProjeto,
    montarCacheProjetos,
    montarEntregasPorEntregaId,
    montarEntregasPorProjetoId,
    normalizarSlideManual,
    obterOuCriarApresentacao,
    resolverSlidesParaExportacao,
    salvarApresentacao,
} from '@/utils/apresentacaoGestaoEntregas'
import { gerarApresentacaoGestaoEntregasPdf } from '@/utils/exportApresentacaoGestaoEntregas'

export default {
    name: 'ModoApresentacaoGestaoEntregas',

    components: {
        SlideApresentacaoRenderer,
        EditorApresentacaoGestao,
    },

    props: {
        aberto: { type: Boolean, default: false },
        idUsuario: { type: [Number, String], default: null },
        periodoInicio: { type: String, default: '' },
        periodoFim: { type: String, default: '' },
        entregasExibidas: { type: Array, default: () => [] },
        fixadasExibidas: { type: Array, default: () => [] },
        atrasadasExibidas: { type: Array, default: () => [] },
        semFimExibidas: { type: Array, default: () => [] },
    },

    emits: ['fechar'],

    data() {
        return {
            config: { version: 1, slides: [] },
            slideAtual: 0,
            editorAberto: false,
            cacheProjetos: {},
            cacheEntregas: {},
            exportando: false,
        }
    },

    computed: {
        listasEntregasPainel() {
            return [
                this.entregasExibidas,
                this.fixadasExibidas,
                this.atrasadasExibidas,
                this.semFimExibidas,
            ]
        },

        contagensPainel() {
            return {
                'entregas-periodo': this.entregasExibidas.length,
                fixadas: this.fixadasExibidas.length,
                atrasadas: this.atrasadasExibidas.length,
                'sem-fim': this.semFimExibidas.length,
            }
        },

        contextoResolucao() {
            return {
                periodoInicio: this.periodoInicio,
                periodoFim: this.periodoFim,
                entregasExibidas: this.entregasExibidas,
                fixadasExibidas: this.fixadasExibidas,
                atrasadasExibidas: this.atrasadasExibidas,
                semFimExibidas: this.semFimExibidas,
                cacheProjetos: this.cacheProjetos,
                cacheEntregas: this.cacheEntregas,
                entregasPorProjetoId: montarEntregasPorProjetoId(this.listasEntregasPainel),
                entregasPorEntregaId: montarEntregasPorEntregaId(this.listasEntregasPainel),
            }
        },

        slidesResolvidos() {
            return resolverSlidesParaExportacao(this.config, this.contextoResolucao)
        },

        slideResolvidoAtual() {
            return this.slidesResolvidos[this.slideAtual] || null
        },
    },

    watch: {
        aberto(val) {
            if (val) {
                this.inicializar()
                document.body.style.overflow = 'hidden'
                document.addEventListener('keydown', this.onKeydownGlobal)
                this.$nextTick(() => this.$refs.overlayEl?.focus())
            } else {
                document.body.style.overflow = ''
                document.removeEventListener('keydown', this.onKeydownGlobal)
            }
        },
    },

    beforeUnmount() {
        document.body.style.overflow = ''
        document.removeEventListener('keydown', this.onKeydownGlobal)
    },

    methods: {
        inicializar() {
            this.config = obterOuCriarApresentacao(this.idUsuario, {
                periodoInicio: this.periodoInicio,
                periodoFim: this.periodoFim,
            })
            this.slideAtual = 0
            this.editorAberto = false
            this.carregarDadosManuais()
        },

        coletarItensManuais() {
            const itens = []
            for (const slide of this.config.slides || []) {
                if (slide.fonte !== 'manual' || slide.tipo !== 'tabela') continue
                const norm = normalizarSlideManual({ ...slide })
                itens.push(...(norm.itensManuais || []))
            }
            return itens
        },

        async carregarDadosManuais() {
            await Promise.all([
                this.carregarProjetosManuais(),
                this.carregarEntregasManuais(),
            ])
        },

        async carregarProjetosManuais() {
            const itens = this.coletarItensManuais()
            const ids = new Set(itens.map((i) => Number(i.projeto_id)).filter((id) => id > 0))
            const faltantes = [...ids].filter((id) => !this.cacheProjetos[id])
            if (!faltantes.length) return

            const novos = { ...this.cacheProjetos }
            await Promise.all(
                faltantes.map(async (id) => {
                    try {
                        const projeto = await serviceProjetos.getProjetoById(id)
                        const mapa = montarCacheProjetos([projeto])
                        Object.assign(novos, mapa)
                    } catch {
                        /* projeto pode não existir mais */
                    }
                }),
            )
            this.cacheProjetos = novos
        },

        async carregarEntregasManuais() {
            const itens = this.coletarItensManuais()
            const entregaIds = new Set(
                itens.map((i) => Number(i.entrega_id)).filter((id) => id > 0),
            )
            const painelMapa = montarEntregasPorEntregaId(this.listasEntregasPainel)
            const faltantesEntrega = [...entregaIds].filter((id) => !this.cacheEntregas[id] && !painelMapa[id])

            const projetoIdsParaBuscar = new Set()
            for (const item of itens) {
                const eid = Number(item.entrega_id)
                const pid = Number(item.projeto_id)
                if (!eid || !pid) continue
                if (this.cacheEntregas[eid] || painelMapa[eid]) continue
                projetoIdsParaBuscar.add(pid)
            }

            if (!faltantesEntrega.length && !projetoIdsParaBuscar.size) return

            const novos = { ...this.cacheEntregas, ...painelMapa }
            await Promise.all(
                [...projetoIdsParaBuscar].map(async (projetoId) => {
                    try {
                        const response = await serviceProjetos.getEntregas(projetoId)
                        const projetoApi = response?.projetos?.[0]
                        Object.assign(novos, montarCacheEntregasDeProjeto(projetoApi))
                    } catch {
                        /* entregas podem não estar disponíveis */
                    }
                }),
            )
            this.cacheEntregas = novos
        },

        onProjetoCache(projeto) {
            const mapa = montarCacheProjetos([projeto])
            this.cacheProjetos = { ...this.cacheProjetos, ...mapa }
        },

        onEntregaCache(projetoApi) {
            if (!projetoApi) return
            const mapa = montarCacheEntregasDeProjeto(projetoApi)
            this.cacheEntregas = { ...this.cacheEntregas, ...mapa }
            const mapaProjeto = montarCacheProjetos([projetoApi])
            this.cacheProjetos = { ...this.cacheProjetos, ...mapaProjeto }
        },

        onSlidesAtualizados(slides) {
            this.config = { ...this.config, slides }
            salvarApresentacao(this.idUsuario, this.config)
            if (this.slideAtual >= slides.length) {
                this.slideAtual = Math.max(0, slides.length - 1)
            }
            this.carregarDadosManuais()
        },

        slideAnterior() {
            if (this.slideAtual > 0) this.slideAtual -= 1
        },

        slideProximo() {
            if (this.slideAtual < this.config.slides.length - 1) this.slideAtual += 1
        },

        onKeydownGlobal(event) {
            if (!this.aberto) return

            const tag = event.target?.tagName?.toLowerCase()
            const editandoTexto = tag === 'input' || tag === 'textarea' || tag === 'select'
                || event.target?.isContentEditable

            if (event.key === 'Escape') {
                if (this.editorAberto) {
                    this.editorAberto = false
                    event.preventDefault()
                } else {
                    this.fechar()
                }
                return
            }

            if (editandoTexto) return

            if (event.key === 'ArrowLeft') {
                event.preventDefault()
                this.slideAnterior()
            }
            if (event.key === 'ArrowRight') {
                event.preventDefault()
                this.slideProximo()
            }
        },

        fechar() {
            this.$emit('fechar')
        },

        async exportarPdf() {
            this.exportando = true
            try {
                await this.carregarDadosManuais()
                const resolvidos = resolverSlidesParaExportacao(this.config, this.contextoResolucao)
                gerarApresentacaoGestaoEntregasPdf(resolvidos)
            } finally {
                this.exportando = false
            }
        },
    },
}
</script>

<style scoped>
.modo-apresentacao-overlay {
    position: fixed;
    inset: 0;
    z-index: 9999;
    display: flex;
    flex-direction: column;
    background: linear-gradient(160deg, #f8fbfb 0%, #eef6f6 50%, #fff 100%);
    color: #222;
}

.apresentacao-topo {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 4.25rem;
    padding: 0.5rem 1.5rem;
    flex-shrink: 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
    background: rgba(255, 255, 255, 0.9);
}

.apresentacao-logo-wrap {
    display: flex;
    align-items: center;
    flex-shrink: 0;
    height: 3.25rem;
    overflow: hidden;
}

.apresentacao-logo {
    height: 5.5rem;
    width: auto;
    max-width: min(420px, 52vw);
    object-fit: contain;
    object-position: left center;
    transform: scale(1.15);
    transform-origin: left center;
}

.apresentacao-topo-acoes {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
}

.apresentacao-corpo {
    flex: 1;
    display: flex;
    min-height: 0;
    overflow: hidden;
}

.apresentacao-corpo--com-editor .apresentacao-slide-area {
    flex: 1;
    min-width: 0;
}

.apresentacao-slide-area {
    flex: 1;
    display: flex;
    align-items: center;
    min-height: 0;
    padding: 0.5rem 0;
}

.apresentacao-slide-container {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-self: stretch;
    min-height: 0;
    padding: 1.5rem 2rem;
    overflow: hidden;
}

.apresentacao-nav {
    flex-shrink: 0;
    box-sizing: border-box;
    width: 2.75rem !important;
    min-width: 2.75rem !important;
    max-width: 2.75rem !important;
    height: 2.75rem !important;
    min-height: 2.75rem !important;
    max-height: 2.75rem !important;
    padding: 0 !important;
    align-self: center;
    background: rgba(255, 255, 255, 0.92) !important;
    background-image: none !important;
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 50% !important;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 1.25rem;
    font-weight: 400 !important;
    color: var(--cor-primaria-forte, #008a88) !important;
    margin: 0 0.35rem;
    line-height: 1;
}

.apresentacao-nav:disabled {
    opacity: 0.35;
    cursor: not-allowed;
}

.apresentacao-nav:not(:disabled):hover {
    background: var(--cor-primaria-fraca, rgba(0, 182, 179, 0.15));
}

.apresentacao-rodape {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    padding: 0.65rem 1rem;
    border-top: 1px solid rgba(0, 0, 0, 0.08);
    background: rgba(255, 255, 255, 0.9);
}

.apresentacao-indicadores {
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem;
    justify-content: center;
}

.apresentacao-bolinha {
    box-sizing: border-box;
    width: 0.55rem !important;
    min-width: 0.55rem !important;
    height: 0.55rem !important;
    padding: 0 !important;
    border-radius: 50% !important;
    border: none;
    background: rgba(0, 0, 0, 0.2) !important;
    color: transparent !important;
    cursor: pointer;
    font-weight: 400 !important;
}

.apresentacao-bolinha--ativa {
    background: var(--cor-primaria, #00b6b3);
    transform: scale(1.2);
}

.apresentacao-contador {
    font-size: 0.85rem;
    color: #555;
    min-width: 4rem;
    text-align: right;
}
</style>
